import { useEffect, useRef } from "react";
import { getDotGridColors } from "@/lib/theme/canvas-theme-colors";
import { useTheme } from "@/lib/theme/theme-provider";

/**
 * DotGridBackground renders the animated dot grid with a wandering glow.
 * It fills its wrapper, so the caller positions it via className
 * (e.g. "fixed inset-0" or "absolute inset-0").
 */
export function DotGridBackground({ className }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { resolvedTheme } = useTheme();

  useEffect(
    function paintDotGridBackground() {
      const canvas = canvasRef.current;
      const wrapper = canvas?.parentElement;
      const ctx = canvas?.getContext("2d");
      if (!canvas || !wrapper || !ctx) return;

      const { background, dotBase, dotGlow } = getDotGridColors(resolvedTheme);
      const spacing = 26;
      const reach = 220;
      const scale = Math.min(window.devicePixelRatio || 1, 2);
      const base = document.createElement("canvas");
      const reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      let dots: { x: number; y: number }[] = [];
      let frame = 0;
      let w = 0;
      let h = 0;

      const resize = () => {
        w = wrapper.clientWidth;
        h = wrapper.clientHeight;
        canvas.width = w * scale;
        canvas.height = h * scale;
        ctx.setTransform(scale, 0, 0, scale, 0, 0);
        dots = [];
        for (let x = spacing / 2; x < w; x += spacing) {
          for (let y = spacing / 2; y < h; y += spacing) {
            dots.push({ x, y });
          }
        }
        // the still grid renders once, frames only repaint the glowing spot
        base.width = canvas.width;
        base.height = canvas.height;
        const baseCtx = base.getContext("2d");
        if (!baseCtx) return;
        baseCtx.setTransform(scale, 0, 0, scale, 0, 0);
        baseCtx.fillStyle = background;
        baseCtx.fillRect(0, 0, w, h);
        baseCtx.fillStyle = dotBase;
        for (const dot of dots) {
          baseCtx.beginPath();
          baseCtx.arc(dot.x, dot.y, 1, 0, 2 * Math.PI);
          baseCtx.fill();
        }
      };

      // one glow spot wanders the grid on a slow looping path
      const draw = (ms: number) => {
        const t = ms / 20000;
        const cx = w * (0.5 + 0.38 * Math.sin(t * 2 * Math.PI * 0.9));
        const cy = h * (0.5 + 0.32 * Math.cos(t * 2 * Math.PI * 0.61));
        ctx.drawImage(base, 0, 0, w, h);
        const pad = reach * 2.2;
        for (const dot of dots) {
          if (Math.abs(dot.x - cx) > pad || Math.abs(dot.y - cy) > pad)
            continue;
          const dx = dot.x - cx;
          const dy = dot.y - cy;
          const glow = Math.exp(-(dx * dx + dy * dy) / (reach * reach));
          const alpha = 0.05 + Math.min(0.3, glow * 0.3);
          const radius = 1 + Math.min(1, glow) * 0.8;
          ctx.beginPath();
          ctx.arc(dot.x, dot.y, radius, 0, 2 * Math.PI);
          ctx.fillStyle = dotGlow(alpha);
          ctx.fill();
        }
      };

      const loop = (ms: number) => {
        draw(ms);
        frame = requestAnimationFrame(loop);
      };

      resize();
      const observer = new ResizeObserver(() => {
        resize();
        // resizing clears the canvas, so repaint when no loop is running
        if (reducedMotion) draw(0);
      });
      observer.observe(wrapper);
      if (reducedMotion) {
        draw(0);
      } else {
        frame = requestAnimationFrame(loop);
      }
      return function cleanupDotGridBackground() {
        cancelAnimationFrame(frame);
        observer.disconnect();
      };
    },
    [resolvedTheme],
  );

  return (
    <div className={className} aria-hidden>
      <canvas ref={canvasRef} className="size-full" />
    </div>
  );
}
